import AppLoader from "../components/AppLoader";
import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams  } from "react-router-dom";
import { useAuth } from "../hooks/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  CircularProgress,
  Button,
  Avatar,
  Rating,
  Card,
  CardContent,
  Chip,
  Grid,
  Fade,
  Grow,
  ClickAwayListener,
  IconButton,
  LinearProgress,
  Tooltip,
  Divider,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import DashboardIcon from '@mui/icons-material/Dashboard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ListAltIcon from "@mui/icons-material/ListAlt";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionIcon from "@mui/icons-material/Description";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import PsychologyIcon from "@mui/icons-material/Psychology";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import InsightsIcon from '@mui/icons-material/Insights';
import PsychologyAltOutlinedIcon from '@mui/icons-material/PsychologyAltOutlined';
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects"; // asegurate de tener esta línea al principio

// BarChart Insights
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CampaignIcon from "@mui/icons-material/Campaign";
import MicIcon from "@mui/icons-material/Mic";
import { ReactComponent as SharkIcon } from "../assets/icons/shark-2.svg";
import { api } from "../services/api";
import { getPitchTypeById } from "../services/pitchTypesService";
import { getAIProfileById } from "../services/aiProfilesService";
import CoachSuggestionText from "../components/CoachSuggestionText";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import CircularAccessBar from "../components/partners/CircularAccessBar";
import AppBackground from "../components/layout/AppBackground";

const SessionButtons = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("evaluation");

  return (
  <Grow in timeout={800}>
    <Box
      sx={{
        textAlign: "center",
        mt: 8,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "center",
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
      }}
    >
      <Button
        variant="outlined"
        onClick={() => navigate("/pitch-start")}
        sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
      >
        <ReplayIcon sx={{ fontSize: 20 }} /> {t("new_session")}
      </Button>
      <Button
        variant="outlined"
        onClick={() => navigate("/sessions")}
        sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
      >
        <ListAltIcon sx={{ fontSize: 20 }} /> {t("back_to_sessions")}
      </Button>
      <Button
        variant="outlined"
        onClick={() => navigate("/dashboard")}
        sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 } }}
      >
        <DashboardIcon sx={{ fontSize: 20 }} /> {t("back_to_dashboard")}
      </Button>
    </Box>
  </Grow>
  );
};

const EvaluationPage = () => {
  const { session_id } = useParams();
  const { token, user } = useAuth();
  const { t, i18n } = useTranslation("evaluation");
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [openLEXTooltip, setOpenLEXTooltip] = useState(false);
  const [openWPMTooltip, setOpenWPMTooltip] = useState(false);
  const [searchParams] = useSearchParams();
  const nedte = searchParams.get("nedte") === "true";

  const navigateToSession = (id) => {
    navigate(`/session/${id}`);
  };

  const [summaryAccordionOpen, setSummaryAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("summaryAccordionOpen");
    return stored ? JSON.parse(stored) : true;
  });
  const handleSummaryAccordionToggle = (_event, isExpanded) => {
    setSummaryAccordionOpen(isExpanded);
    localStorage.setItem("summaryAccordionOpen", JSON.stringify(isExpanded));
  };

  const [metricsAccordionOpen, setMetricsAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("metricsAccordionOpen");
    return stored ? JSON.parse(stored) : true;
  });
  const handleMetricsAccordionToggle = () => {
    const newValue = !metricsAccordionOpen;
    setMetricsAccordionOpen(newValue);
    localStorage.setItem("metricsAccordionOpen", JSON.stringify(newValue));
  };


  const [coachAccordionOpen, setCoachAccordionOpen] = useState(() => {
    const stored = localStorage.getItem("coachAccordionOpen");
    return stored ? JSON.parse(stored) : true; // abierto por defecto
  });
  const handleCoachAccordionToggle = () => {
    const newValue = !coachAccordionOpen;
    setCoachAccordionOpen(newValue);
    localStorage.setItem("coachAccordionOpen", JSON.stringify(newValue));
  };


  const [sessionData, setSessionData] = useState({ pitchType: null, aiProfile: null });
  const loadSessionPitchData = async () => {
    try {
      const { pitch_type_id, ai_profile_id } = evaluation.session;

      const [pt, ap] = await Promise.all([
        getPitchTypeById(pitch_type_id),
        getAIProfileById(ai_profile_id)
      ]);

      setSessionData({ pitchType: pt, aiProfile: ap });
    } catch (err) {
      console.error("Error loading pitch or profile:", err);
    }
  };


  const getLexicalColor = (value) => {
    if (value >= 0.6) return "success";     // verde
    if (value >= 0.3) return "warning";     // amarillo
    return "error";                         // rojo
  };

  useEffect(() => {
    if (nedte) {
      setLoading(false); // ✅ importante para salir del loader
      return;
    }
    const fetchEvaluation = async () => {
      try {
        // const res = await fetch(`${process.env.REACT_APP_API_URL}/api/pitch-evaluations/${session_id}`, {
        //   headers: { Authorization: `Bearer ${token}` },
        // });
        // const data = await res.json();
        // if (res.ok) {
        //   setEvaluation(data);
        // }
        const res = await api.get(`/api/pitch-evaluations/${session_id}`);
        setEvaluation(res.data);
      } catch (err) {
        console.error("❌ Error loading evaluation:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEvaluation();
  }, [session_id, token]);

  useEffect(() => {
    if (evaluation?.session) {
      loadSessionPitchData();
    }
  }, [evaluation]);

  const getIconForPitchType = (name) => {
    const lower = name.toLowerCase();

    if (lower.includes("elevator")) return <TrendingUpIcon />;
    if (lower.includes("demo")) return <CampaignIcon />;
    if (lower.includes("shark")) return <SharkIcon style={{ width: 22, height: 22 }} />;
    if (lower.includes("media") || lower.includes("medio")) return <MicIcon />;

    return <RocketLaunchIcon />;
  };

  const previousSessionId = evaluation?.session?.previous_session_id;
  const nextSessionId = evaluation?.session?.next_session_id;

  const [openTranscriptModal, setOpenTranscriptModal] = useState(false);
  const [openPDFConfirm, setOpenPDFConfirm] = useState(false);
  const handleDownloadPDF = () => {
    // TODO: conectar con endpoint y descargar
    console.log("📄 Descargar PDF de la sesión");
  };

  const [transcriptData, setTranscriptData] = useState([]);
  const [transcriptDate, setTranscriptDate] = useState(null);
  const [loadingTranscript, setLoadingTranscript] = useState(true); // Nuevo estado para manejar la carga
  const [transcriptLoadingError, setTranscriptLoadingError] = useState(false); // Estado para error de carga

  const fetchTranscript = async () => {
    if (transcriptData.length > 0) return; // Si ya está cargada, no hacemos la solicitud
    setLoadingTranscript(true);
    try {
      const res = await api.get(`/api/pitch-sessions/${session_id}/transcript`);
      setTranscriptData(res.data.transcript);
      setTranscriptDate(res.data.start_time);
      setTranscriptLoadingError(false); // Se limpia el error si la carga es exitosa
    } catch (err) {
      console.error("❌ Error loading transcript:", err);
      setTranscriptLoadingError(true); // Se marca el error si falla la carga
    } finally {
      setLoadingTranscript(false); // Se marca como no cargando al finalizar
    }
  };

  const renderLanguageBadge = (lang) => {
    const label = lang === "es" ? "ES" : lang === "en" ? "EN" : "🌐";
    return (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          px: 1,
          py: 0.2,
          borderRadius: 1,
          fontSize: "0.75rem",
          fontWeight: "bold",
          backgroundColor: "#555",
          color: "#fff",
          ml: 0.5,
        }}
      >
        {label}
      </Box>
    );
  };

  useEffect(() => {
    // Limpiar datos del transcript al cambiar de sesión
    setTranscriptData([]);
    setTranscriptDate(null);
    setLoadingTranscript(true);
    setTranscriptLoadingError(false);
  }, [session_id]);

  if (loading) return <AppLoader message={t("loading_evaluation")} />;

  // Si es una evaluación de "nedte" (no hay suficiente contenido para evaluar la sesion)
  if (nedte) {
    return (
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Fade in timeout={700}>
          <Box
            sx={{
              backgroundColor: "rgba(0,0,0,0.7)",
              borderRadius: 4,
              maxWidth: { xs: 500, md: 700 },
              p: 4,
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Typography variant="h5" gutterBottom>
              {t("title")}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 3 }}>
              {t("not_enough_content")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t("not_enough_content_hint")}
            </Typography>
            <SessionButtons />
          </Box>
        </Fade>
      </AppBackground>
    );
  }
  if (!evaluation) {
    return (
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Fade in timeout={700}>
          <Box
            sx={{
              backgroundColor: "rgba(0,0,0,0.7)",
              borderRadius: 4,
              maxWidth: { xs: 500, md: 700 },
              p: 4,
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Typography variant="h5" gutterBottom>
              {t("title")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t("not_found")}
            </Typography>
            <SessionButtons />
          </Box>
        </Fade>
      </AppBackground>
    );
  }
  if (!evaluation.evaluation) {
    return (
      <AppBackground
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Fade in timeout={700}>
          <Box
            sx={{
              backgroundColor: "rgba(0,0,0,0.7)",
              borderRadius: 4,
              maxWidth: { xs: 500, md: 700 },
              p: 4,
              textAlign: "center",
              color: "#fff",
            }}
          >
            <Typography variant="h5" gutterBottom>
              {t("title")}
            </Typography>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t("not_ready_yet")}
            </Typography>
            <SessionButtons />
          </Box>
        </Fade>
      </AppBackground>
    );
  }

  const { evaluation: evalData, session } = evaluation;
  const avatarUrl = session?.ai_profile_image;
  const aiName = i18n.language?.startsWith("es") ? session?.ai_profile_name_es : session?.ai_profile_name_en;
  const pitchType = i18n.language?.startsWith("es") ? session?.pitch_type_name_es : session?.pitch_type_name_en;
  const durationMin = Math.round(session.duration_seconds / 60);
  const userWords = session.user_word_count;
  const wpm_user = evalData.wpm_user;
  const lexical_diversity = evalData.lexical_diversity;
  const aiWords = session.ai_word_count;
  const aiRatio = Math.round(session.ai_speech_ratio * 100);
  const formattedDate = new Intl.DateTimeFormat(i18n.language?.startsWith("es") ? "es-AR" : "en-US", {
      dateStyle: "short",
      timeStyle: "short",
      hourCycle: "h23",
    }).format(new Date(session.start_time));

  if (typeof evalData.summary_bullets === "string") {
    try {
      evalData.summary_bullets = JSON.parse(evalData.summary_bullets);
    } catch (e) {
      console.warn("❌ No se pudo parsear summary_bullets:", e);
      evalData.summary_bullets = [];
    }
  }
  const getSummaryBulletMeta = (item) => {
    const text = typeof item === "string" ? item.trim() : "";

    if (text.startsWith("+")) {
      return { text: text.slice(1).trim(), kind: "positive", iconColor: "#4ade80" };
    }

    if (text.startsWith("-")) {
      return { text: text.slice(1).trim(), kind: "warning", iconColor: "#db745f" };
    }

    if (text.startsWith("=")) {
      return { text: text.slice(1).trim(), kind: "neutral", iconColor: "#a78bfa" };
    }

    return null;
  };
  const getWPMColor = (wpm) => {
    if (wpm < 90) return "error.main";
    if (wpm <= 130) return "success.main";
    if (wpm <= 160) return "warning.main";
    return "error.dark";
  };

  const handleGoToPublicHome = () => {
    if (!sessionData.pitchType || !sessionData.aiProfile) return;
    navigate("/pitch-start", {
      state: {
        pitchType: sessionData.pitchType,
        aiProfile: sessionData.aiProfile,
      },
    });
  };

  return (
    <>
    {user?.is_partner_user && user?.source === "circular" && (
          <CircularAccessBar />
    )}

    <AppBackground
      sx={{
        color: "#fff",
      }}
    >
      <Fade in timeout={700}>
        <Box
          sx={{
            backgroundColor: "rgba(0,0,0,0.75)",
            borderRadius: 4,
            maxWidth: 800,
            mx: "auto",
            px: { xs: 2, md: 3 },
            py: { xs: 3, md: 4 },
    position: "relative", // ✅ necesario

          }}
        >

          {/* Botones Navegacion entre sesiones */}
          <Box
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              display: "flex",
              gap: 0,
              zIndex: 10,
              backgroundColor: "rgba(255,255,255,0.05)",
              borderRadius: 4,
              p: 0
            }}
          >
            <IconButton
              onClick={() => {
                if (previousSessionId) navigate(`/evaluation/${previousSessionId}`);
              }}
              sx={{ color: previousSessionId ? "#fff" : "#666" }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            <IconButton
              onClick={() => {
                if (nextSessionId) navigate(`/evaluation/${nextSessionId}`);
              }}
              sx={{ color: nextSessionId ? "#fff" : "#666" }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>

          {/* Encabezado */}
          <Box sx={{ mb: 2 }}>
              {/* Título */}
              <Box
                sx={{
                  position: "relative",
                  mb: 4,
                }}
              >
                {/* Íconos arriba a la derecha */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 30,
                    right: -4,
                    display: "flex",
                    gap: 0,
                  }}
                >
                  <Tooltip title={t("view_transcript")}>
                    <IconButton
                      onClick={() => {
                        fetchTranscript();
                        setOpenTranscriptModal(true);
                      }}
                      sx={{ color: "#fff" }}
                    >
                      <ArticleOutlinedIcon />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title={t("download_pdf")}> */}
                    {/* <IconButton onClick={() => setOpenPDFConfirm(true)} sx={{ color: "#fff" }}> */}
                    <IconButton sx={{ color: "#808080", opacity: 0.3 }}>
                      <PictureAsPdfIcon />
                    </IconButton>
                  {/* </Tooltip> */}
                </Box>

                {/* Título con espacio a la derecha para que no lo tapen los íconos */}
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    pr: 10, // deja espacio para los íconos (ajustable según tamaño)
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {t("title")}
                </Typography>
              </Box>
              {/* Grid de resumen superior */}
              <Grow in timeout={800}>
                <Box
                    sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 3,
                    px: 2,
                    mb: 1,
                    }}
                >
                    {/* Columna 1: Global score */}
                    <Box sx={{ textAlign: "center", flex: 1 }}>
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                          <CircularProgress
                          variant="determinate"
                          value={(evalData.global_score / 5) * 100}
                          size={100}
                          thickness={4}
                          sx={{ color: "#8e44ad" }}
                          />
                          <Box
                          sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#fff",
                          }}
                          >
                          <Typography variant="h6">{evalData.global_score?.toFixed(1)} / 5</Typography>
                          </Box>
                      </Box>
                    </Box>

                    {/* Columna 2: Pitch type + AI */}
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center", gap: 1 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            bgcolor: "#a855f7",
                            color: "#fff",
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {getIconForPitchType(pitchType)}
                        </Box>
                        <Typography variant="body2">{pitchType}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={avatarUrl}
                          alt={aiName}
                          sx={{ width: 34, height: 34 }}
                        />
                        <Typography variant="body2">{aiName}</Typography>
                      </Box>
                    </Box>
                </Box>
              </Grow>
              {/* Columna 3: Fecha e idioma */}
              <Grow in timeout={900}>
                <Box sx={{ textAlign: "center", flex: 1 }}>
                  <Typography variant="body2">
                    {formattedDate} {renderLanguageBadge(session.language)}
                  </Typography>
                </Box>
              </Grow>
              {/* Métricas técnicas */}
              <Grow in timeout={1000}>
                <Box
                    sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    py: 1.5,
                    mt: 4,
                    }}
                >
                  <Box>
                    <Typography variant="body2">⏱️ {t("duration")}</Typography>
                    <Typography variant="caption">{durationMin} min</Typography>
                    </Box>
                    <Box>
                    <Typography variant="body2">💬 {t("words")}</Typography>
                    <Typography variant="caption">{userWords } / {userWords + aiWords}</Typography>
                    </Box>
                    <Box>
                    <Typography variant="body2">🎙️ {t("user_speaking")}</Typography>
                    {/* <Typography variant="caption">{(aiRatio/100).toFixed(1)}%</Typography> */}
                    <Typography variant="caption">{(100-(aiRatio/100)).toFixed(1)}%</Typography>
                  </Box>
                </Box>
              </Grow>
              {/* WPM y Lex Div */}
              <Grow in timeout={1100}>
                <Box
                    sx={{
                    display: "flex",
                    justifyContent: "space-around",
                    textAlign: "center",
                    backgroundColor: "rgba(255,255,255,0.05)",
                    borderRadius: 2,
                    py: 1.5,
                    mt: 2,
                    px: 1,
                    gap: 1.5,
                    }}
                >
                  {/* WPM y Diversidad Lexica */}
                  <Box sx={{ textAlign: "center" }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                      {/* 🗣️ */}
                      <Typography variant="body2">{t("wpm_user")}</Typography>
                      <ClickAwayListener onClickAway={() => setOpenWPMTooltip(false)}>
                        <Box display="inline">
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption">
                                  {t("wpm_user_explained_1")}<br />
                                  {t("wpm_user_explained_2")}<br />
                                  {t("wpm_user_explained_3")}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                            open={openWPMTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <IconButton size="small" onClick={() => setOpenWPMTooltip((prev) => !prev)}>
                              <InfoOutlinedIcon sx={{ color: "#ccc" }} fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ClickAwayListener>
                    </Box>
                    {/* <Typography variant="caption">{wpm_user?.toFixed(0)}</Typography> */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Box
                        sx={{
                          height: 18,
                          minWidth: 10,
                          width: "fit-content",
                          backgroundColor: getWPMColor(wpm_user), // hex o theme palette
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          color: '#fff',
                          px: 1.5
                        }}
                      >
                        {wpm_user.toFixed(0)} WPM
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ minWidth: 100 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                      {/* 🧠 */}
                      <Typography variant="body2">{t("lexical_diversity")}</Typography>
                      <ClickAwayListener onClickAway={() => setOpenLEXTooltip(false)}>
                        <Box display="inline">
                          <Tooltip
                            title={
                              <Box>
                                <Typography variant="caption">
                                  {t("lexical_diversity_explained_1")}<br />
                                  {t("lexical_diversity_explained_2")}<br />
                                  {t("lexical_diversity_explained_3")}<br />
                                  {t("lexical_diversity_explained_4")}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                            arrow
                            open={openLEXTooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                          >
                            <IconButton size="small" onClick={() => setOpenLEXTooltip((prev) => !prev)}>
                              <InfoOutlinedIcon sx={{ color: "#ccc" }} fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </ClickAwayListener>
                    </Box>
                    <Box sx={{ position: 'relative', width: '100%' }}>
                      <LinearProgress
                        variant="determinate"
                        value={lexical_diversity * 100}
                        color={getLexicalColor(lexical_diversity)}
                        sx={{
                          height: 18,
                          borderRadius: 2,
                          backgroundColor: "#333"
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {(lexical_diversity * 100).toFixed(0)}%
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grow>
          </Box>

          {/* Summary */}
          <Grow in timeout={1200}>
            <Accordion
              expanded={summaryAccordionOpen}
              onChange={handleSummaryAccordionToggle}
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                borderRadius: 2,
                mt: 4,
                mb: 3,
                "&:before": { display: "none" }, // quita la línea gris superior por defecto
                overflow: "hidden",               // evita recortes visuales
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "#fff",
                      mt: "0px", // 💡 levanta la flecha para alinearla con el título
                    }}
                  />
                }
                sx={{
                  px: 0,
                  color: "#fff",
                  alignItems: "flex-start",  // ✅ alinea el ícono arriba
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    margin: 0,
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                  },
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DescriptionIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t("summary")}
                </Typography>
                <Typography variant="body2">
                  {evalData.general_summary}
                </Typography>

              </AccordionSummary>
              {/* Summary Bullets */}
              <AccordionDetails sx={{ px: 0, pt: 0 }}>

                <Typography
                  variant="subtitle1"
                  sx={{
                    mt: 2,
                    mb: 1.2,
                    color: "#f4e8ff",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    letterSpacing: "0.01em",
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 18, color: "#c084fc" }} />
                  {t("summary_bullets")}
                </Typography>

                {Array.isArray(evalData.summary_bullets) && evalData.summary_bullets.length > 0 ? (
                  <Box sx={{ pl: 0, mr: 1, mt: 1 }}>
                    {evalData.summary_bullets.map((item, index) => {
                      const meta = getSummaryBulletMeta(item) || {
                        text: typeof item === "string" ? item.trim() : "",
                        iconColor: "rgba(255, 255, 255, 0.72)",
                      };

                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1.2,
                            mb: 0.8,
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 16,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              pt: 0.7,
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: "50%",
                                backgroundColor: meta.iconColor,
                              }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: "#fff" }}>
                            {meta.text}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#fff", pl: 3, mr: 1, mt: 1 }}>
                    ---
                  </Typography>
                )}

                {/* AI Feedback */}
                {evalData.aiprofile_comments && (
                  <Box mt={2} mb={0}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('aiprofile_comments_title', {aiprofile: aiName})}
                    </Typography>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      "{evalData.aiprofile_comments}"
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Grow>

          <Divider sx={{ mt: 1, mb: 1,borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* CTA de coaching - guia general para la proxima sesion */}
          {evalData.coaching_cta && (

            <Grow in timeout={1300}>
              <Box mt={2} mb={2}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 1, color: "#FFD700" }}
                    // FFD700  FFB300
                  >
                    <TipsAndUpdatesIcon fontSize="small" sx={{ color: "#FFD700" }} /> {t(`coach_cta_title`)}
                  </Typography>
                <Typography variant="body2">{evalData.coaching_cta}</Typography>
              </Box>
            </Grow>
          )}

          {/* Coach Suggestion - metrica en la que trabajar */}
          {evalData.coach_suggestion && (
            <Grow in timeout={1400}>
              <Box mt={3} mb={3.5} backgroundColor="rgba(255, 255, 255, 0.05)" pt={1} pb={0} px={1} borderRadius={2}>
                <Accordion
                  // defaultExpanded
                  expanded={coachAccordionOpen}
                  onChange={handleCoachAccordionToggle}
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: "transparent",
                    borderRadius: 2,
                    mt: 0,
                    mb: 0,
                    pb: 0,
                    "&:before": { display: "none" }, // quita la línea gris superior por defecto
                    overflow: "hidden",               // evita recortes visuales
                  }}
                >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: "#fff",
                        mt: "0px", // 💡 levanta la flecha para alinearla con el título
                      }}
                    />
                  }
                  sx={{
                    px: 0,
                    pb: 0,
                    mb: 0,
                    color: "#fff",
                    alignItems: "flex-start",  // ✅ alinea el ícono arriba
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                      margin: 0,
                      "&.Mui-expanded": {
                        margin: 0,
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#a855f7",  mb: 0, py: 0  }}
                  >
                    <PsychologyAltOutlinedIcon fontSize="small" sx={{ color: "#a855f7", mb: 0, py: 0 }} /> {t(`coach_suggestion_title`)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1, pt: 0, mt: 0 }}>
                  <CoachSuggestionText suggestedMetric={evalData.coach_suggestion} />

                  <Button
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: '20px', mt: 2, textTransform: 'none' }}
                    onClick={handleGoToPublicHome}
                  >
                    {t(`coach_suggestion_button`)}
                  </Button>
                </AccordionDetails>
              </Accordion>
              </Box>
            </Grow>
          )}

          <Divider sx={{ mt: 1, mb: 1,borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Fortalezas */}
          <Grow in timeout={1500}>
            <Box mt={3}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                <ThumbUpAltIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t("strengths")}
              </Typography>
              <Typography variant="body2">{evalData.strengths}</Typography>
            </Box>
          </Grow>

          {/* Areas de mejora */}
          <Grow in timeout={1500}>
            <Box mt={4} mb={3}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                <PsychologyIcon  fontSize="small" sx={{ color: "#a855f7" }} /> {t("improvement_areas")}
              </Typography>
              <Typography variant="body2">{evalData.improvement_areas}</Typography>
            </Box>
          </Grow>

          <Divider sx={{ mt: 1, mb: 1,borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Métrica del objetivo*/}
          <Grow in timeout={1500}>
            <Box mt={3}>
            {/* <Card sx={{ backgroundColor: "#1c1c1c", height: "100%", minHeight: 90, mb: 2 }}>
                <CardContent> */}
                  <Box display="flex" alignItems="center" justifyContent="left" mb={0} gap={2}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <TrackChangesIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t(`scores.objective_achievement`)}
                    </Typography>
                  </Box>
                  <Box ml={3.2}> {/* Ajustá el valor hasta que quede alineado con la "L" */}
                    <Rating
                        value={parseFloat(evalData[`objective_achievement_score`])}
                        readOnly
                        precision={0.1}
                        sx={{ color: "#4CAF50" }}  // Verde motivador
                        icon={<CheckCircleIcon fontSize="inherit" />}
                        emptyIcon={<CheckCircleOutlineIcon fontSize="inherit" />}
                      />
                  </Box>
                  {/* </Box> */}
                  <Typography variant="body2">{evalData[`objective_achievement_comment`]}</Typography>
                {/* </CardContent>
              </Card> */}
            </Box>
          </Grow>

          {/* Evaluacion por criterios (Metricas) */}
          <Grow in timeout={1500}>
            <Accordion
              expanded={metricsAccordionOpen}
              onChange={handleMetricsAccordionToggle}
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                borderRadius: 2,
                mt: 2,
                mb: 3,
                "&:before": { display: "none" }, // quita la línea gris superior por defecto
                overflow: "hidden",               // evita recortes visuales
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "#fff",
                      mt: "0px", // 💡 levanta la flecha para alinearla con el título
                    }}
                  />
                }
                sx={{
                  pt: 2,
                  pb: 0,
                  px: 0,
                  color: "#fff",
                  alignItems: "flex-start",  // ✅ alinea el ícono arriba
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    margin: 0,
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                  },
                }}
              >
                  {/* <Typography variant="subtitle1" fontWeight="bold">
                    {t(`scores.title`)}
                  </Typography> */}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <AssessmentIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t(`scores.title`)}
                  </Typography>
                  <Typography variant="body2">{t(`scores.title_detail`)}</Typography>

              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pt: 1, pb: 0, mb: 0 }}>
                <Grid container spacing={0.5} px={0} pt={0} pb={0} mb={0} mt={0} >
                  {["clarity", "relevance", "engagement", "fluency"].map((key) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <Card sx={{ backgroundColor: "#1c1c1c", height: "100%", minHeight: 120 }}>
                        <CardContent sx={{ mt: 0.5, mb: 0, py: 0, px: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 0, mb: 0, py: 0 }}>
                            {t(`scores.${key}`)}
                          </Typography>
                          <Rating
                            value={parseFloat(evalData[`${key}_score`])}
                            readOnly
                            precision={0.1}
                            sx={{ color: "#FFD700", mb: 0 }}
                          />
                          <Typography variant="body2" sx={{ mt: 0, mb: 0, py: 0 }}>{evalData[`${key}_comment`]}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grow>


          {/* Autoevaluacion*/}
          {session.user_self_rating > 0 && (
            <Grow in timeout={1500}>
              <Box>
                <Divider sx={{ mt: 1, mb: 1,borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    backgroundColor: "transparent",
                    borderRadius: 2,
                    mt: 2,
                    mb: 3,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: "#fff",
                          mt: "0px",
                        }}
                      />
                    }
                    sx={{
                      pt: 1,
                      pb: 0,
                      px: 0,
                      color: "#fff",
                      alignItems: "flex-start",
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        margin: 0,
                        "&.Mui-expanded": {
                          margin: 0,
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0, py: 0 }}
                    >
                      <EmojiObjectsIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t("self_evaluation.title")}
                    </Typography>
                    <Typography variant="body2">{t("self_evaluation.subtitle")}</Typography>
                  </AccordionSummary>

                  <AccordionDetails sx={{ px: 1, pt: 0, mt: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Círculo del score */}
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        <CircularProgress
                          variant="determinate"
                          value={(session.user_self_rating / 10) * 100}
                          size={60}
                          thickness={4}
                          sx={{ color: "#FFD700" }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {session.user_self_rating}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Reflexión del usuario */}
                      {session.user_self_comment && (
                        <Typography
                          variant="body2"
                          fontStyle="italic"
                          sx={{ color: "#ccc", flex: 1, minWidth: 200 }}
                        >
                          "{session.user_self_comment}"
                        </Typography>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Grow>
          )}

          <Divider sx={{ mt: 1, mb: 1,borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Feedback Avanzado */}
          <Grow in timeout={1500}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                borderRadius: 2,
                mt: 2,
                mb: 1,
                "&:before": { display: "none" }, // quita la línea gris superior por defecto
                overflow: "hidden",               // evita recortes visuales
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{
                      color: "#fff",
                      mt: "0px", // 💡 levanta la flecha para alinearla con el título
                    }}
                  />
                }
                sx={{
                  pt: 1,
                  pb: 0,
                  px: 0,
                  color: "#fff",
                  alignItems: "flex-start",  // ✅ alinea el ícono arriba
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    margin: 0,
                    "&.Mui-expanded": {
                      margin: 0,
                    },
                  },
                }}
              >
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <InsightsIcon fontSize="small" sx={{ color: "#a855f7" }} /> {t(`advanced_feedback`)}
                  </Typography>
                  <Typography variant="body2">{t(`advanced_feedback_subtitle`)}</Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 0, pt: 0}}>
                  {/* <Typography variant="body2">{t(`scores.title_detail`)}</Typography> */}
                {evalData.structure_feedback && (
                  <Box mt={2} mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('structure_feedback_title')}
                    </Typography>
                    <Typography variant="body2">{evalData.structure_feedback}</Typography>
                  </Box>
                )}

                {evalData.rephrasing_example && typeof evalData.rephrasing_example === 'object' && (
                  <Box mt={2} mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('rephrasing_example_title')}
                    </Typography>

                    <Box mb={2}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        {t('rephrasing_original')}:
                      </Typography>
                      <Typography variant="body2" fontStyle="italic" color="rgba(243, 172, 172, 0.8)">
                        "{evalData.rephrasing_example.original}"
                      </Typography>

                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        {t('rephrasing_better')}:
                      </Typography>
                      <Typography variant="body2" fontStyle="italic" color="rgba(177, 238, 141, 0.8)">
                        "{evalData.rephrasing_example.better}"
                      </Typography>
                    </Box>

                    <Typography variant="body2" >
                      {evalData.rephrasing_example.why}
                    </Typography>

                  </Box>
                )}

                {/* filler words */}
                {Object.entries(JSON.parse(evalData.filler_words_list || '{}')).length > 0 && (
                  <Box mt={2} mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('filler_words')}
                    </Typography>
                    {Object.entries(JSON.parse(evalData.filler_words_list || '{}')).map(([word, count], idx) => (
                      <Chip
                        key={idx}
                        label={`${word} (${count})`}
                        size="medium"
                        sx={{
                          fontSize: "0.8rem",       // aumenta tamaño de texto
                          height: 30,             // altura total del chip
                          px: 2,                  // padding horizontal
                          borderRadius: 2,        // bordes más suaves
                          backgroundColor: "#333",// fondo sutil si estás en dark mode
                          color: "#fff",          // texto blanco para contraste
                          mr: 1,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* weak words */}
                {Object.entries(JSON.parse(evalData.weak_words_list || '{}')).length > 0 && (
                  <Box mt={2} mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('weak_words')}
                    </Typography>
                    {Object.entries(JSON.parse(evalData.weak_words_list || '{}')).map(([word, count], idx) => (
                      <Chip
                        key={idx}
                        label={`${word} (${count})`}
                        size="medium"
                        sx={{
                          fontSize: "0.8rem",       // aumenta tamaño de texto
                          height: 30,             // altura total del chip
                          px: 2,                  // padding horizontal
                          borderRadius: 2,        // bordes más suaves
                          backgroundColor: "#333",// fondo sutil si estás en dark mode
                          color: "#fff",          // texto blanco para contraste
                          mr: 1,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* repeated words */}
                {Object.entries(JSON.parse(evalData.repetition_top_words || '{}')).length > 0 && (
                  <Box mt={2} mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('repeated_words')}
                    </Typography>
                    {Object.entries(JSON.parse(evalData.repetition_top_words || '{}')).map(([word, count], idx) => (
                      <Chip
                        key={idx}
                        label={`${word} (${count})`}
                        size="medium"
                        sx={{
                          fontSize: "0.8rem",       // aumenta tamaño de texto
                          height: 30,             // altura total del chip
                          px: 2,                  // padding horizontal
                          borderRadius: 2,        // bordes más suaves
                          backgroundColor: "#333",// fondo sutil si estás en dark mode
                          color: "#fff",          // texto blanco para contraste
                          mr: 1,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* initial words */}
                {Object.entries(JSON.parse(evalData.initial_word_usage || '{}')).length > 0 && (
                  <Box mt={2} mb={1}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom color='rgba(255, 255, 255, 0.6)'>
                      {t('initial_words')}
                    </Typography>
                    {Object.entries(JSON.parse(evalData.initial_word_usage || '{}')).map(([word, count], idx) => (
                      <Chip
                        key={idx}
                        label={`${word} (${count})`}
                        size="medium"
                        sx={{
                          fontSize: "0.8rem",       // aumenta tamaño de texto
                          height: 30,             // altura total del chip
                          px: 2,                  // padding horizontal
                          borderRadius: 2,        // bordes más suaves
                          backgroundColor: "#333",// fondo sutil si estás en dark mode
                          color: "#fff",          // texto blanco para contraste
                          mr: 1,
                          mb: 0.5,
                        }}
                      />
                    ))}
                  </Box>
                )}
                {/* user speaking time */}
                {evalData.user_speaking_time && (
                  <Box mt={2}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                      color="rgba(255, 255, 255, 0.6)"
                    >
                      {t('user_speaking_time')}
                    </Typography>
                    <Typography variant="body2">
                      {t('user_speaking_time_formatted', {
                        min: Math.floor(evalData.user_speaking_time / 60),
                        sec: Math.round(evalData.user_speaking_time % 60),
                      })}
                    </Typography>
                  </Box>
                )}

              </AccordionDetails>
            </Accordion>
          </Grow>

          {/* BUTTONS */}
          <SessionButtons />
        </Box>
      </Fade>

      {/* View Session Trasncript */}
      {openTranscriptModal && (
        <Box
          sx={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 2000,
            backgroundColor: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
          onClick={() => setOpenTranscriptModal(false)}
        >
          <Box
            sx={{
              backgroundColor: "#1e1e1e",
              color: "#fff",
              borderRadius: 3,
              maxWidth: 700,
              width: "100%",
              maxHeight: "90vh",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar siempre fijo */}
            <IconButton
              onClick={() => setOpenTranscriptModal(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 15,
                color: "#ccc",
                zIndex: 10,
              }}
            >
              ✕
            </IconButton>


            {/* Contenido scrollable */}
            <Box
              sx={{
                overflowY: "auto",
                px: 3,
                pt: 3,
                pb: 2,
              }}
            >


            {/* Título */}
            <Typography
              variant="h6"
              fontWeight="bold"
              gutterBottom
              sx={{ pr: 4 }}
            >
              {t("transcript_title")}
            </Typography>

            {/* Subtítulo con Pitch Type y AI Profile */}
            <Typography
              variant="subtitle2"
              fontWeight="medium"
              sx={{ pr: 4, mt: -1, mb: 1 }}
            >
              {t("transcript_subtitle", {
                pitchType: i18n.language?.startsWith("es")
                  ? session?.pitch_type_name_es
                  : session?.pitch_type_name_en,
                aiProfile: i18n.language?.startsWith("es")
                  ? session?.ai_profile_name_es
                  : session?.ai_profile_name_en,
              })}
            </Typography>

            {/* Fecha */}
            <Typography
              variant="caption"
              sx={{ pr: 4, color: "#ccc" }}
            >
              {new Date(transcriptDate).toLocaleString(
                i18n.language?.startsWith("es") ? "es-AR" : "en-US",
                { dateStyle: "short", timeStyle: "short", hourCycle: "h23" }
              )}
            </Typography>

            {/* Transcripción formateada */}
            {loadingTranscript ? (
              <Typography variant="body2" color="gray">
                {t("loading_transcript")} {/* Mensaje de "Cargando transcripción" */}
              </Typography>
            ) : transcriptLoadingError ? (
              <Typography variant="body2" color="gray">
                {t("transcript_not_available")} {/* Mensaje de error si no se pudo cargar */}
              </Typography>
            ) : (
              <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                {transcriptData.map((item, index) => {
                  const [role, content] = Object.entries(item)[0];
                  const isUser = role === "user";
                  const bubbleColor = isUser ? "#333" : "#8e44ad";

                  const { user } = useAuth();
                  const userName = user?.profile_name || user?.first_name || "You";
                  const name = isUser
                    ? userName
                    : i18n.language?.startsWith("es")
                      ? session?.ai_profile_name_es
                      : session?.ai_profile_name_en;
                  return (
                    <Box
                      key={index}
                      sx={{
                        alignSelf: role === "user" ? "flex-end" : "flex-start",
                        backgroundColor: role === "user" ? "#333" : "#8e44ad",
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        maxWidth: "90%",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        sx={{ color: "#ccc", display: "block", mb: 0.5 }}
                      >
                        {name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#fff", whiteSpace: "pre-line" }}
                      >
                        {content}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          </Box>
        </Box>
      )}

      {/* Download PDF */}
      {openPDFConfirm && (
        <Box
          sx={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Box sx={{ backgroundColor: "#222", py: 3, px: 2, borderRadius: 2, color: "#fff", maxWidth: 300 }}>
            <Typography variant="h6" gutterBottom>
              {t("confirm_download_title")}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {t("confirm_download_text")}
            </Typography>
            <Box mt={2} display="flex" justifyContent="center" gap={2}>
              <Button onClick={() => setOpenPDFConfirm(false)} variant="outlined" color="secondary">
                {t("confirm_download_cancel")}
              </Button>
              <Button
                onClick={() => {
                  setOpenPDFConfirm(false);
                  handleDownloadPDF(); // función a definir
                }}
                variant="contained"
                color="primary"
              >
                {t("confirm_download_ok")}
              </Button>
            </Box>
          </Box>
        </Box>
      )}

    </AppBackground>
    </>
  );
};

export default EvaluationPage;
